import type { CarrierCarrierInfo } from '@shipium-corp/shipium-ts-types';
import { CarrierStatuses } from '../../services/CarrierService/types';

type ServiceMethodLike = {
  status?: string;
  serviceMethodId?: string;
};

type CarrierLike = {
  status?: string;
  serviceMethods?: ServiceMethodLike[];
  returnsServiceMethods?: ServiceMethodLike[];
};

const isServiceMethodAvailable = (serviceMethodInfo: ServiceMethodLike, isAdmin = false) => {
  return (
    serviceMethodInfo.status === CarrierStatuses.AVAILABLE ||
    (serviceMethodInfo.status === CarrierStatuses.AVAILABLE_SHIPIUM && isAdmin)
  );
};

export const getAvailableCarriers = <C extends CarrierLike>(
  carriers: Record<string, C>,
  isAdmin = false,
): Record<string, C> => {
  return Object.entries(carriers).reduce<Record<string, C>>(
    (prev, [key, carrierInfo]) => {
      if (
        carrierInfo.status === CarrierStatuses.AVAILABLE ||
        (carrierInfo.status === CarrierStatuses.AVAILABLE_SHIPIUM && isAdmin)
      ) {
        prev[key] = {
          ...carrierInfo,
          serviceMethods: carrierInfo.serviceMethods?.filter((serviceMethodInfo) =>
            isServiceMethodAvailable(serviceMethodInfo, isAdmin),
          ),
          returnsServiceMethods: carrierInfo.returnsServiceMethods?.filter((serviceMethodInfo) =>
            isServiceMethodAvailable(serviceMethodInfo, isAdmin),
          ),
        };
      }
      return prev;
    },
    {} as Record<string, C>,
  );
};

export const getAvailableMethods = <C extends CarrierLike>(
  carriers: Record<string, C>,
  { isAdmin, withReturnServiceMethods }: { isAdmin: boolean; withReturnServiceMethods: boolean },
): Record<string, NonNullable<C['serviceMethods']>[number]> => {
  type M = NonNullable<C['serviceMethods']>[number];
  const result: Record<string, M> = {};
  const collect = (methods: readonly M[] | undefined) => {
    for (const method of methods ?? []) {
      if (method.serviceMethodId) {
        result[method.serviceMethodId] = method;
      }
    }
  };
  for (const carrier of Object.values(getAvailableCarriers(carriers, isAdmin))) {
    collect(carrier.serviceMethods as M[] | undefined);
    if (withReturnServiceMethods) {
      collect(carrier.returnsServiceMethods as M[] | undefined);
    }
  }
  return result;
};

/**
 * Service methods selectable for a carrier on a RETURNS fulfillment context.
 *
 * Carriers that declare native return service methods use those. Carriers without one
 * (e.g. Purolator, whose returns run the standard/forward path plus the G2 returns-data push)
 * fall back to their forward serviceMethods — matching getAvailableMethods, which already treats
 * forward methods as valid on returns FCs. Without this fallback a no-native-return carrier
 * contributes zero options to the returns-FC picker and cannot be configured.
 */
export const getReturnsSelectableMethods = <C extends CarrierLike>(
  carrier: C,
): NonNullable<C['serviceMethods']> => {
  const returns = carrier.returnsServiceMethods ?? [];
  return (returns.length > 0 ? returns : (carrier.serviceMethods ?? [])) as NonNullable<C['serviceMethods']>;
};

export const getCredentialStructureName = (
  carrierMap: Record<string, CarrierCarrierInfo>,
  carrierId: string | undefined,
  credentialStructureId: string | undefined,
): string => {
  if (!carrierId || !credentialStructureId || !carrierMap) return credentialStructureId || '';

  const carrier = carrierMap[carrierId];
  const credentialStructure = carrier?.credentialStructures?.find(
    (cs) => cs.credentialStructureId === credentialStructureId,
  );

  return credentialStructure?.name || credentialStructureId;
};

export const getCredentialPropertyName = (
  carrierMap: Record<string, CarrierCarrierInfo>,
  carrierId: string | undefined,
  credentialStructureId: string | undefined,
  credentialPropertyId: string,
): string => {
  if (!carrierId || !credentialStructureId || !carrierMap) return credentialPropertyId;

  const carrier = carrierMap[carrierId];
  const credentialStructure = carrier?.credentialStructures?.find(
    (cs) => cs.credentialStructureId === credentialStructureId,
  );
  const credentialProperty = credentialStructure?.credentialProperties?.find(
    (cp) => cp.credentialId === credentialPropertyId,
  );

  return credentialProperty?.name || credentialPropertyId;
};
